
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Briefcase } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { getLabel } from '@/utils/textUtils';
import { Link } from 'react-router-dom';

export interface ProfessionOptionLabels {
  id: string;
  label: string;
  label_en?: string;
  label_fr?: string;
  label_ru?: string;
}

interface ProfessionSelectProps {
  profession: string;
  professions: ProfessionOptionLabels[];
  searchTerm: string;
  filteredProfessions: ProfessionOptionLabels[];
  openPopover: boolean;
  setOpenPopover: (open: boolean) => void;
  setSearchTerm: (term: string) => void;
  onChange: (name: string, value: string) => void;
  // A trade asked for (e.g. from the URL) that has no professionals yet
  unavailable?: ProfessionOptionLabels | null;
}

const ProfessionSelect: React.FC<ProfessionSelectProps> = ({
  profession,
  professions,
  searchTerm,
  filteredProfessions,
  openPopover,
  setOpenPopover,
  setSearchTerm,
  onChange,
  unavailable,
}) => {
  const { t, i18n } = useTranslation();
  const { direction } = useDirection();
  const currentLang = i18n.language;

  // Build professions with display names and searchable text from all languages
  const displayProfessions = useMemo(() => {
    return filteredProfessions.map(prof => ({
      ...prof,
      displayName: getLabel(prof, currentLang),
      // Combine all language variants for searching
      searchValue: [
        prof.label,
        prof.label_en,
        prof.label_fr,
        prof.label_ru
      ].filter(Boolean).join(' ')
    }));
  }, [filteredProfessions, currentLang]);

  // Find the selected profession and get its display name
  const selectedProfession = professions.find(p => p.id === profession);
  const selectedLabel = selectedProfession
    ? getLabel(selectedProfession, currentLang)
    : t('requestForm.selectProfession');

  // The contact form, filled in, so asking for a missing trade takes one tap
  const contactLink = (trade: string) => '/contact?' + new URLSearchParams({
    subject: t('requestForm.noSupplySubject'),
    message: t('requestForm.noSupplyMessage', { trade: trade.trim() || '…' }),
  }).toString();
  const unavailableLabel = unavailable ? getLabel(unavailable, currentLang) : '';

  return (
    <div className="space-y-2">
      <Label htmlFor="profession" className="text-gray-700">{t('requestForm.selectProfession')}</Label>
      <Popover open={openPopover} onOpenChange={setOpenPopover}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={openPopover} aria-label={t('requestForm.selectProfession')} className="w-full justify-between text-right pr-3 pl-10 relative rounded-lg">
            {selectedLabel}
            <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full min-w-[230px] bg-white shadow-lg z-50 p-0 border border-gray-300" align="start" side="bottom" avoidCollisions={false} style={{
          overflow: 'visible'
        }}>
          <Command dir={direction}>
            <CommandInput placeholder={t('requestForm.searchProfession')} value={searchTerm} onValueChange={setSearchTerm} className="bg-white" />
            <CommandEmpty className="space-y-2 p-4 text-sm">
              <p>{t('requestForm.noSupplyYetSearch')}</p>
              <Link to={contactLink(searchTerm)} className="font-semibold text-teal-700 underline underline-offset-4">
                {t('requestForm.noSupplyCta')}
              </Link>
            </CommandEmpty>
            <CommandList className="bg-white z-50 max-h-64 overflow-auto border-gray-200">
              <CommandGroup>
                {displayProfessions.map(professionOption => (
                  <CommandItem
                    key={professionOption.id}
                    value={professionOption.searchValue}
                    onSelect={() => {
                      onChange('profession', professionOption.id);
                      setSearchTerm('');
                      setOpenPopover(false);
                    }}
                  >
                    {professionOption.displayName}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {unavailable && !profession && (
        <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          {t('requestForm.noSupplyYet', { trade: unavailableLabel })}{' '}
          <Link to={contactLink(unavailableLabel)} className="font-semibold underline underline-offset-4">
            {t('requestForm.noSupplyCta')}
          </Link>
        </p>
      )}
    </div>
  );
};

export default ProfessionSelect;
